import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Tooltip from "@mui/material/Tooltip";
import { GLYPH_STATES, cadenceLabel, windowLabel } from "./taskHubUtils";

// Completion grid: one row per routine, one column per period, one glyph per
// cell. Click a cell with an occurrence to open that day's task.

function CellGlyph({ cell }) {
  const st = GLYPH_STATES[cell.state] || GLYPH_STATES.open;
  const multi = (cell.total_slots || 0) > 1;
  // No occurrence that day (a Monday-only routine on a Tuesday, or a day
  // before the task was activated): draw nothing. A circle reads as "open".
  if (!cell.total_slots) {
    return <span className="w-[26px] h-[26px] inline-block" aria-hidden="true" />;
  }
  // A single-slot cell is a fixed circle; a cell that repeats within the day
  // carries "done/total" and stretches into a pill so two-digit counts fit.
  const shape = multi
    ? "min-w-[26px] h-[26px] px-1.5 rounded-full text-[10px]"
    : "w-[26px] h-[26px] rounded-full text-[11px]";
  return (
    <span
      className={`${shape} border-2 ${st.border} ${st.bg} ${st.text} inline-flex items-center justify-center font-bold tabular-nums whitespace-nowrap`}
    >
      {multi ? (
        `${cell.done_slots}/${cell.total_slots}`
      ) : cell.state === "done" || cell.state === "late" ? (
        <CheckIcon style={{ fontSize: 14 }} />
      ) : cell.state === "missed" ? (
        <CloseIcon style={{ fontSize: 14 }} />
      ) : cell.state === "waiting" ? (
        <ScheduleIcon style={{ fontSize: 14 }} />
      ) : null}
    </span>
  );
}

const cellTitle = (cell) => {
  const st = GLYPH_STATES[cell.state] || GLYPH_STATES.open;
  const bits = [st.label];
  if ((cell.total_slots || 0) > 1) bits.push(`${cell.done_slots}/${cell.total_slots} slots`);
  if (cell.date) bits.push(cell.date);
  return bits.join(" · ");
};

export default function OverviewGrid({ data, taskByUuid, onCellPress }) {
  const columns = data?.columns || [];
  const rows = data?.rows || [];
  const gridCols = `minmax(220px,2.1fr) repeat(${columns.length}, 72px) 150px`;

  return (
    <div>
      <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-x-auto">
        <div style={{ minWidth: 220 + columns.length * 72 + 150 + 32 }}>
          <div
            className="grid gap-2.5 items-end px-4 py-2.5 border-b border-slate-200 text-[12px] font-semibold text-slate-700"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span>Task</span>
            {columns.map((c) => (
              <span
                key={c.period_key}
                className={`text-center ${c.is_current ? "text-violet-700" : ""}`}
              >
                <span
                  className={`block text-[10px] font-medium uppercase tracking-wide ${
                    c.is_current ? "text-violet-700" : "text-slate-400"
                  }`}
                >
                  {c.sub}
                </span>
                {c.label}
              </span>
            ))}
            <span className="text-right">Completion</span>
          </div>
          {rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              No active tasks on this cadence. Activate one in the Library to start tracking it here.
            </div>
          ) : (
            rows.map((r) => {
              const pct = r.pct;
              const color =
                pct == null ? "text-slate-400" : pct >= 90 ? "text-emerald-600" : pct >= 70 ? "text-amber-700" : "text-rose-600";
              const bar =
                pct == null ? "bg-slate-300" : pct >= 90 ? "bg-emerald-600" : pct >= 70 ? "bg-amber-600" : "bg-rose-600";
              return (
                <div
                  key={r.plan_task.uuid}
                  className="grid gap-2.5 items-center px-4 py-3 border-b border-slate-100 last:border-b-0"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-slate-900 truncate">
                      {r.plan_task.name}
                    </span>
                    <span className="block text-[11px] text-slate-500 truncate">
                      {[
                        cadenceLabel(r.plan_task),
                        // Overview rows carry a task summary without the window
                        // fields; the library payload has them.
                        windowLabel(taskByUuid?.[r.plan_task.uuid] || r.plan_task),
                      ].join(" · ")}
                    </span>
                  </span>
                  {r.cells.map((cell, i) => {
                    const col = columns[i];
                    const pressable = !!cell.total_slots && !!cell.date;
                    if (!cell.total_slots) {
                      // Nothing to hover or click on a day the routine does not run.
                      return (
                        <span
                          key={cell.period_key}
                          className={`flex items-center justify-center py-1 rounded-lg ${col?.is_current ? "bg-violet-50/60" : ""}`}
                        >
                          <CellGlyph cell={cell} />
                        </span>
                      );
                    }
                    return (
                      <span
                        key={cell.period_key}
                        className={`flex items-center justify-center py-1 rounded-lg ${col?.is_current ? "bg-violet-50/60" : ""}`}
                      >
                        <Tooltip title={cellTitle(cell)}>
                          <button
                            type="button"
                            disabled={!pressable}
                            onClick={() => pressable && onCellPress(r.plan_task, cell)}
                            // flex, not inline: an empty glyph has no text baseline,
                            // so an inline button would sit lower than one with an icon.
                            className={`flex items-center justify-center leading-none ${
                              pressable ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"
                            }`}
                          >
                            <CellGlyph cell={cell} />
                          </button>
                        </Tooltip>
                      </span>
                    );
                  })}
                  <span className="flex items-center justify-end gap-2">
                    <span className="w-16 h-1.5 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
                      <span className={`block h-full rounded-full ${bar}`} style={{ width: `${pct || 0}%` }} />
                    </span>
                    <span className={`text-[13px] font-semibold tabular-nums w-[74px] text-right ${color}`}>
                      {r.done_count}/{r.expected_count}
                      {pct != null ? ` · ${pct}%` : ""}
                    </span>
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap mt-3 px-1 text-[12px] text-slate-600">
        {Object.entries(GLYPH_STATES).map(([key, st]) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <CellGlyph cell={{ state: key, total_slots: 1, done_slots: key === "done" || key === "late" ? 1 : 0 }} />
            {st.label}
          </span>
        ))}
      </div>
    </div>
  );
}
